import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import dayjs from "dayjs";

import axios from "../../../api/axios";
import { SETTLEMENTS_PROFITABILITY_CHARTS_ENDPOINT } from "../../../consts";
import { formatNumber } from "../../../utils";
import {
  ProfitabilityChartsResponse,
  ProfitabilityExtremeRow,
  ProfitabilityGroupBy,
  ProfitabilityTrendPoint,
} from "../../../types/profitability";
import ChartCard from "../../ui/charts/chartCard.component";
import { buildChartSx, useChartPalette } from "../../ui/charts/chartPalette";
import { formatAxisNumber } from "../../ui/charts/chartFormatters";

interface ProfitabilityChartsProps {
  /** Selected month, as YYYY-MM. Undefined while the month picker is empty. */
  month?: string;
  groupBy: ProfitabilityGroupBy;
  search?: string;
}

/** A month label short enough to sit on a 12-slot axis without colliding. */
const formatMonthTick = (month: string) => dayjs(`${month}-01`).format("MM.YY");

const toNumber = (value: string | null): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Trim a long entity label for a bar's axis. The full label stays in the
 * tooltip, so nothing is lost - this only stops one long name from eating the
 * plot area.
 */
const truncateLabel = (label: string, max = 22) =>
  label.length > max ? `${label.slice(0, max - 1)}…` : label;

export default function ProfitabilityCharts({
  month,
  groupBy,
  search,
}: ProfitabilityChartsProps) {
  const { t } = useTranslation();
  const palette = useChartPalette();
  const chartSx = useMemo(() => buildChartSx(palette), [palette]);

  const [data, setData] = useState<ProfitabilityChartsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchCharts = useCallback(async () => {
    if (!month) {
      setData(null);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(SETTLEMENTS_PROFITABILITY_CHARTS_ENDPOINT, {
        params: { month, group_by: groupBy, ...(search ? { search } : {}) },
      });
      setData(response.data as ProfitabilityChartsResponse);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [month, groupBy, search]);

  useEffect(() => {
    fetchCharts();
  }, [fetchCharts]);

  // Memoised rather than defaulted inline: a fresh [] on every render would
  // re-run every series memo below it.
  const trend: ProfitabilityTrendPoint[] = useMemo(() => data?.trend ?? [], [data]);
  const monthLabels = useMemo(() => trend.map((point) => point.month), [trend]);
  const marginSeries = useMemo(
    () => trend.map((point) => toNumber(point.profitability_net)),
    [trend]
  );

  const trendIsEmpty = trend.length === 0;

  /**
   * How the selected month's revenue splits. Revenue = cost + margin, so the
   * pie has a real whole; plotting revenue and cost as two slices of one pie
   * would invent a total (revenue + cost) that means nothing.
   *
   * When cost overran revenue the whole flips: the month's cost is what got
   * covered plus what did not, because a negative slice cannot be drawn.
   */
  const selectedPoint = useMemo(
    () => trend.find((point) => point.month === month) ?? null,
    [trend, month]
  );

  const revenueSplit = useMemo(() => {
    if (!selectedPoint || selectedPoint.settlement_count === 0) return null;

    const revenue = toNumber(selectedPoint.profit);
    const cost = toNumber(selectedPoint.profitability_cost);
    const margin = toNumber(selectedPoint.profitability_net);

    if (margin >= 0) {
      return {
        loss: false,
        total: revenue,
        slices: [
          { id: "cost", value: cost, label: t("Cost"), color: palette.series(1) },
          {
            id: "margin",
            value: margin,
            label: t("Profitability (net)"),
            color: palette.series(0),
          },
        ].filter((slice) => slice.value > 0),
      };
    }

    return {
      loss: true,
      total: cost,
      slices: [
        {
          id: "covered",
          value: revenue,
          label: t("Covered by revenue"),
          color: palette.series(0),
        },
        {
          id: "uncovered",
          value: Math.abs(margin),
          label: t("Uncovered loss"),
          color: palette.diverging.negative,
        },
      ].filter((slice) => slice.value > 0),
    };
  }, [selectedPoint, palette, t]);

  // Worst first, then best, so the chart reads as one ranking from the rows
  // that need attention up to the ones that do not.
  const extremes: ProfitabilityExtremeRow[] = useMemo(() => {
    if (!data) return [];
    return [...data.extremes.worst, ...[...data.extremes.best].reverse()];
  }, [data]);

  /**
   * What a bar is called. The backend already picks the identifying field per
   * layer (serial for equipment); the contract layer's bare number needs the
   * "Contract no." prefix to read as one, and that prefix is UI text, so it is
   * applied here rather than baked into the API.
   */
  const labelOf = useCallback(
    (row: ProfitabilityExtremeRow) => {
      const label = row.label || "-";
      return groupBy === "contract" ? `${t("Contract no.")} ${label}` : label;
    },
    [groupBy, t]
  );

  // The band axis is keyed on row id, not on the label: two pieces of
  // equipment can share a display name, and duplicate band values would
  // collapse into one another. The label is attached by the formatter below.
  const extremeKeys = useMemo(() => extremes.map((row) => row.id), [extremes]);
  const labelsByKey = useMemo(
    () =>
      extremes.reduce<Record<string, string>>((accumulator, row) => {
        accumulator[row.id] = labelOf(row);
        return accumulator;
      }, {}),
    [extremes, labelOf]
  );
  const extremeValues = useMemo(
    () => extremes.map((row) => toNumber(row.profitability_net)),
    [extremes]
  );

  const layerTitle = {
    equipment: t("Best and worst equipment"),
    contract: t("Best and worst contracts"),
    client: t("Best and worst clients"),
  }[groupBy];

  const layerAxisLabel = {
    equipment: t("Serial number"),
    contract: t("Contract"),
    client: t("Client"),
  }[groupBy];

  const money = (value: number | null) =>
    value === null ? "" : `${formatNumber(value)} zł`;

  const monthAxisLabel = t("Month");
  const amountAxisLabel = t("Amount (zł)");

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "2fr 3fr" },
        gap: 2,
        width: "100%",
      }}
    >
      <ChartCard
        title={t("Revenue split")}
        subtitle={
          revenueSplit?.loss
            ? t("Cost exceeded revenue in the selected month")
            : t("How the selected month's revenue divides")
        }
        height={280}
        loading={loading}
        isEmpty={!revenueSplit || revenueSplit.slices.length === 0}
        emptyMessage={t("No confirmed settlements with profitability in this period.")}
        action={
          month ? (
            <Chip
              size="small"
              variant="outlined"
              label={dayjs(`${month}-01`).format("MM.YYYY")}
            />
          ) : undefined
        }
      >
        {/* A pie needs a meaningful whole, so the slices are the parts revenue
            actually divides into - never revenue against cost, which would sum
            to nothing real. */}
        <PieChart
          height={280}
          margin={{ left: 12, right: 12, top: 8, bottom: 8 }}
          series={[
            {
              data: revenueSplit?.slices ?? [],
              innerRadius: 52,
              paddingAngle: 2,
              cornerRadius: 4,
              highlightScope: { fade: "global", highlight: "item" },
              valueFormatter: (slice: { value: number }) => {
                const total = revenueSplit?.total || 0;
                const share = total ? (slice.value / total) * 100 : 0;
                return `${money(slice.value)} (${formatNumber(share)}%)`;
              },
            },
          ]}
          sx={{
            ...chartSx,
            // A 2px surface gap between slices rather than a drawn border.
            "& .MuiPieArc-root": { stroke: palette.surface, strokeWidth: 2 },
          }}
        />
      </ChartCard>

      <ChartCard
        title={t("Monthly margin")}
        subtitle={t("Profitability (net) per month")}
        height={280}
        loading={loading}
        isEmpty={trendIsEmpty}
        emptyMessage={t("No confirmed settlements with profitability in this period.")}
      >
        <BarChart
          height={280}
          margin={{ left: 12, right: 20, top: 8, bottom: 8 }}
          grid={{ horizontal: true }}
          hideLegend
          borderRadius={4}
          xAxis={[
            {
              scaleType: "band",
              data: monthLabels,
              label: monthAxisLabel,
              height: 58,
              valueFormatter: (value: string, context) =>
                context.location === "tick"
                  ? formatMonthTick(value)
                  : dayjs(`${value}-01`).format("MM.YYYY"),
              // The selected month is set apart by a much lighter/darker step
              // of its own pole rather than by a third hue: the bar still has
              // to read as profit or loss first, and a hue that clears both
              // blue and red in both themes does not exist without borrowing
              // one that means "good" or "bad".
              colorMap: {
                type: "ordinal",
                values: monthLabels,
                colors: monthLabels.map((label, index) => {
                  const negative = marginSeries[index] < 0;
                  if (label === month) {
                    return negative
                      ? palette.divergingCurrent.negative
                      : palette.divergingCurrent.positive;
                  }
                  return negative
                    ? palette.diverging.negative
                    : palette.diverging.positive;
                }),
              },
            },
          ]}
          yAxis={[
            {
              // Wide enough for a six-figure tick plus the rotated axis label.
              width: 104,
              label: amountAxisLabel,
              valueFormatter: formatAxisNumber,
            },
          ]}
          series={[
            {
              data: marginSeries,
              label: t("Profitability (net)"),
              valueFormatter: money,
            },
          ]}
          sx={chartSx}
        />
      </ChartCard>

      <Box sx={{ gridColumn: { xs: "auto", lg: "1 / -1" } }}>
        <ChartCard
          title={layerTitle}
          subtitle={t("Ranked by margin, worst first")}
          height={Math.max(220, extremes.length * 34 + 90)}
          loading={loading}
          isEmpty={extremes.length === 0}
          emptyMessage={t("No confirmed settlements with profitability in this period.")}
          action={
            month ? (
              <Chip
                size="small"
                variant="outlined"
                label={dayjs(`${month}-01`).format("MM.YYYY")}
              />
            ) : undefined
          }
        >
          {/* Horizontal: entity labels are long, and a horizontal bar gives each
              one a full-width label instead of a rotated, clipped tick. */}
          <BarChart
            layout="horizontal"
            height={Math.max(220, extremes.length * 34 + 90)}
            margin={{ left: 12, right: 16, top: 8, bottom: 8 }}
            grid={{ vertical: true }}
            hideLegend
            borderRadius={4}
            yAxis={[
              {
                scaleType: "band",
                data: extremeKeys,
                width: 220,
                label: layerAxisLabel,
                // Truncated on the axis so one long label cannot eat the plot,
                // but left whole in the tooltip, where there is room for it.
                valueFormatter: (key: string, context) =>
                  context.location === "tick"
                    ? truncateLabel(labelsByKey[key] ?? "-")
                    : (labelsByKey[key] ?? "-"),
              },
            ]}
            xAxis={[
              {
                label: amountAxisLabel,
                height: 58,
                valueFormatter: formatAxisNumber,
                // Few, widely spaced ticks: this axis is as wide as the card,
                // and the default step would draw a gridline every few pixels.
                tickNumber: 6,
                colorMap: {
                  type: "piecewise",
                  thresholds: [0],
                  colors: [palette.diverging.negative, palette.diverging.positive],
                },
              },
            ]}
            series={[
              {
                data: extremeValues,
                label: t("Profitability (net)"),
                valueFormatter: (value: number | null, { dataIndex }) => {
                  const row = extremes[dataIndex];
                  const percent = row?.profitability_percent;
                  const amount = money(value);
                  return percent === null || percent === undefined
                    ? amount
                    : `${amount} (${formatNumber(percent)}%)`;
                },
              },
            ]}
            sx={chartSx}
          />
        </ChartCard>
      </Box>
    </Box>
  );
}
